import { useState, useEffect } from "react"
import { Link, useNavigate } from "react-router-dom"
import { Activity, Mail, Loader2 } from "lucide-react"
import { Button } from "@/shared/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { authService } from "@/shared/services/auth"
import { useToast } from "@/shared/hooks/useToast"

/**
 * 忘记密码页面
 * 发送验证码到邮箱
 */
export default function ForgotPassword() {
  const navigate = useNavigate()
  const { showError, showSuccess } = useToast()

  const [email, setEmail] = useState("")
  const [isLoading, setIsLoading] = useState(false)
  const [countdown, setCountdown] = useState(0)
  const [error, setError] = useState<string | null>(null)

  // 倒计时效果
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    // 基础验证
    if (!email.trim()) {
      setError("请输入邮箱地址")
      return
    }

    // 邮箱格式验证
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setError("请输入有效的邮箱地址")
      return
    }

    setIsLoading(true)
    try {
      const result = await authService.forgotPassword(email.trim())
      if (result.success) {
        showSuccess("验证码已发送，请查收邮件")
        setCountdown(60) // 60秒倒计时
        // 3秒后跳转到重置密码页面
        setTimeout(() => {
          navigate(`/reset-password?email=${encodeURIComponent(email.trim())}`)
        }, 2000)
      } else {
        setError(result.error || '发送验证码失败，请稍后重试')
        showError(result.error || '发送验证码失败，请稍后重试')
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '发送验证码失败，请稍后重试'
      setError(errorMessage)
      showError(errorMessage)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo和标题 */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
            <Activity className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight mb-2">忘记密码</h1>
          <p className="text-slate-500 dark:text-slate-400">
            输入您的邮箱地址，我们将发送验证码
          </p>
        </div>

        {/* 忘记密码卡片 */}
        <Card className="border-0 shadow-xl backdrop-blur-sm bg-white/80 dark:bg-slate-900/80">
          <CardHeader className="space-y-1 pb-6">
            <CardTitle className="text-2xl font-semibold">重置密码</CardTitle>
            <CardDescription>
              验证码将发送到您的邮箱，有效期10分钟
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSendCode} className="space-y-5">
              {/* 邮箱输入 */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  邮箱地址
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value)
                      setError(null)
                    }}
                    className="pl-10 h-11 rounded-lg border-slate-200 dark:border-slate-800 focus:border-primary focus:ring-2 focus:ring-primary/20"
                    disabled={isLoading}
                    autoComplete="email"
                    required
                  />
                </div>
              </div>

              {/* 错误提示 */}
              {error && (
                <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3">
                  <p className="text-sm text-destructive">{error}</p>
                </div>
              )}

              {/* 提交按钮 */}
              <Button
                type="submit"
                className="w-full h-11 rounded-lg text-base font-medium shadow-sm hover:shadow-md transition-all"
                disabled={isLoading || countdown > 0}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    发送中...
                  </>
                ) : countdown > 0 ? (
                  `${countdown}秒后可重新发送`
                ) : (
                  "发送验证码"
                )}
              </Button>
            </form>

            {/* 返回登录链接 */}
            <div className="mt-6 text-center">
              <Link
                to="/login"
                className="text-sm text-primary font-medium hover:underline"
              >
                返回登录
              </Link>
            </div>
          </CardContent>
        </Card>

        {/* 底部说明 */}
        <p className="mt-6 text-center text-xs text-slate-400 dark:text-slate-500">
          如未收到邮件，请检查垃圾邮件文件夹
        </p>
      </div>
    </div>
  )
}

